import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Link,
  CircularProgress,
  InputAdornment,
  IconButton,
  Card,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';

const MAROON = '#7b0011';
const GOLD = '#c89428';
const CREAM = '#fdf3e4';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const resetState = location.state as { email?: string; newPassword?: string } | null;
  // TEMPORARY: OTP verification step is bypassed.
  // const isVerificationStep = location.pathname.endsWith('/verify');
  const isVerificationStep = false;
  const [email, setEmail] = useState(resetState?.email || '');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState(resetState?.newPassword || '');
  const [confirmPassword, setConfirmPassword] = useState(resetState?.newPassword || '');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(isVerificationStep);
  const [resendSeconds, setResendSeconds] = useState(isVerificationStep ? 60 : 0);

  useEffect(() => {
    if (isVerificationStep && !resetState?.email) navigate('/forgot-password', { replace: true });
  }, [isVerificationStep, navigate, resetState?.email]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setInterval(() => setResendSeconds((seconds) => Math.max(0, seconds - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const requestOtp = async () => {
    if (!email) {
      toast.error('Enter your registered email');
      return;
    }
    try {
      setLoading(true);
      // TEMPORARY: Keep request-otp flow for rollback.
      // await api.post('/auth/forgot-password/request-otp', { email });
      // setOtpSent(true);
      // setResendSeconds(60);
      // toast.success('If the email is registered, a reset code has been sent.');
      // if (!isVerificationStep) navigate('/forgot-password/verify', { state: { email, newPassword } });
      toast.success('You can reset your password directly right now.');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Unable to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const startReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !newPassword || !confirmPassword) {
      toast.error('Please complete all fields');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    try {
      setLoading(true);
      // TEMPORARY: Backend currently bypasses OTP verification.
      await api.post('/auth/forgot-password/confirm', { email, otp: '000000', new_password: newPassword });
      toast.success('Password reset successfully. Please sign in.');
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Unable to reset password');
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) return toast.error('Enter the 6-digit reset code');
    try {
      setLoading(true);
      await api.post('/auth/forgot-password/confirm', { email, otp, new_password: newPassword });
      toast.success('Password reset successfully. Please sign in.');
      navigate('/login');
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Unable to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: CREAM,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          backgroundImage: 'url(/maandvo-hero-banner.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          opacity: 0.15,
          zIndex: 0,
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Card
          sx={{
            width: '100%',
            maxWidth: 520,
            margin: '0 auto',
            padding: { xs: 3, md: 4 },
            borderRadius: 2,
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
            backgroundColor: '#fff',
          }}
        >
          <Box sx={{ textAlign: 'center', marginBottom: 3 }}>
            <Typography
              sx={{
                fontSize: '1.9rem',
                fontWeight: 700,
                color: MAROON,
                fontFamily: "'Georgia', 'Times New Roman', serif",
                marginBottom: 1,
              }}
            >
              Forgot Password
            </Typography>
            <Typography variant="body2" sx={{ color: '#8a7a6d', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
              {otpSent ? `Enter the reset code sent to ${email}` : 'Enter your email and choose a new password'}
            </Typography>
          </Box>

          <form onSubmit={otpSent ? confirmReset : startReset}>
            <TextField
              fullWidth
              label="Email"
              placeholder="Enter your registered email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              variant="outlined"
              margin="normal"
              InputProps={{
                readOnly: otpSent,
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: MAROON }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  '& fieldset': { borderColor: '#e8d4b0' },
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: MAROON },
                },
              }}
            />
            {!otpSent && <TextField
              fullWidth
              label="New Password"
              placeholder="At least 8 characters"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              type={showNewPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: MAROON }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword((prev) => !prev)} edge="end" sx={{ color: MAROON }}>
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  '& fieldset': { borderColor: '#e8d4b0' },
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: MAROON },
                },
              }}
            />}

            {!otpSent && <TextField
              fullWidth
              label="Confirm Password"
              placeholder="Enter your new password again"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type={showConfirmPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: MAROON }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword((prev) => !prev)} edge="end" sx={{ color: MAROON }}>
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: "'Georgia', 'Times New Roman', serif",
                  '& fieldset': { borderColor: '#e8d4b0' },
                  '&:hover fieldset': { borderColor: GOLD },
                  '&.Mui-focused fieldset': { borderColor: MAROON },
                },
              }}
            />}

            {otpSent && <TextField fullWidth label="Reset Code" placeholder="Enter 6-digit code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} margin="normal" autoFocus inputProps={{ inputMode: 'numeric', maxLength: 6 }} />}

            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{
                mt: 2,
                backgroundColor: MAROON,
                color: '#fff9f2',
                padding: '12px',
                fontSize: '1rem',
                fontFamily: "'Georgia', 'Times New Roman', serif",
                fontWeight: 700,
                textTransform: 'uppercase',
                borderRadius: 1,
                '&:hover': { backgroundColor: '#5d000d' },
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#fff9f2' }} /> : otpSent ? 'Verify Code & Reset Password' : 'Continue'}
            </Button>

            {otpSent && <Button fullWidth variant="text" onClick={requestOtp} disabled={loading || resendSeconds > 0} sx={{ mt: 1.25, color: MAROON }}>{resendSeconds > 0 ? `Resend Reset Code in ${resendSeconds}s` : 'Resend Reset Code'}</Button>}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#8a7a6d', fontFamily: "'Georgia', 'Times New Roman', serif" }}>
                Back to sign in?{' '}
                <Link
                  onClick={() => navigate('/login')}
                  sx={{
                    color: MAROON,
                    fontWeight: 700,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Sign In
                </Link>
              </Typography>
            </Box>
          </form>
        </Card>
      </Container>
    </Box>
  );
};

export default ForgotPasswordPage;
